import {useEffect, useRef, useState} from 'react';

const useSwiperNavigation = ({onSlideChange, onLastVisibleChange} = {}) => {
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const swiperRef = useRef(null);

    const [isBeginning, setIsBeginning] = useState(true);
    const [isAtEnd, setIsAtEnd] = useState(false);

    const isLastSlideVisible = (swiper) => {
        if (!swiper?.slides?.length || !swiper.el) return false;

        const lastSlide = swiper.slides[swiper.slides.length - 1];
        const container = swiper.el;

        if (!lastSlide || !container) return false;

        const slideRect = lastSlide.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        return slideRect.right <= containerRect.right && slideRect.left >= containerRect.left;
    };

    const updateBoundaries = (swiper) => {
        if (!swiper) return;

        const progress = swiper.progress;
        const isBeginningNow = progress <= 0;
        const isAtEndNow = isLastSlideVisible(swiper);

        setIsBeginning((prev) => (prev !== isBeginningNow ? isBeginningNow : prev));
        setIsAtEnd((prev) => (prev !== isAtEndNow ? isAtEndNow : prev));
        onLastVisibleChange?.(isAtEndNow);
    };

    const swiperOptions = {
        slidesPerView: 'auto',
        onInit: (swiper) => {
            swiperRef.current = swiper;
            requestAnimationFrame(() => {
                updateBoundaries(swiper);
            });
        },
        onSlideChange: (swiper) => {
            onSlideChange?.(swiper);
            updateBoundaries(swiper);
        },
        onProgress: (swiper) => {
            updateBoundaries(swiper);
        },
        onSetTranslate: (swiper) => {
            updateBoundaries(swiper);
        },
        onTransitionEnd: (swiper) => {
            updateBoundaries(swiper);
        },
    };

    // Custom navigation click handlers
    const handlePrevClick = () => {
        if (!isBeginning && swiperRef.current) {
            swiperRef.current.slidePrev();
        }
    };

    const handleNextClick = () => {
        if (!isAtEnd && swiperRef.current) {
            swiperRef.current.slideNext();
        }
    };

    useEffect(() => {
        const prevButton = prevRef.current;
        const nextButton = nextRef.current;

        prevButton?.addEventListener('click', handlePrevClick);
        nextButton?.addEventListener('click', handleNextClick);

        return () => {
            prevButton?.removeEventListener('click', handlePrevClick);
            nextButton?.removeEventListener('click', handleNextClick);
        };
    }, [isBeginning, isAtEnd]);

    useEffect(() => {
        const handleResize = () => {
            if (swiperRef.current) {
                swiperRef.current.update();
                requestAnimationFrame(() => {
                    updateBoundaries(swiperRef.current);
                });
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return {
        prevRef,
        nextRef,
        isBeginning,
        isAtEnd,
        swiperOptions,
    };
};

export default useSwiperNavigation;
